/** Fetch wrapper of the API: same-origin "/api" in every environment (see vite.config.ts). */
import type { ApiErrorCode, ApiErrorResponse, HealthResponse } from '@k-management/shared';

export type { HealthResponse };

/** An API error with the code and field errors sent by the server. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode | 'network-error' | 'unknown',
    message: string,
    readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
  }
}

type TokenProvider = () => Promise<string | null>;
let tokenProvider: TokenProvider = async () => null;

/** Registered by AuthProvider: every request carries the token of the signed-in user. */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

export async function apiRequest<T>(
  path: string,
  options: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown } = {},
): Promise<T> {
  const response = await send(path, options.method ?? 'GET', options.body);
  return (response.status === 204 ? undefined : await response.json()) as T;
}

async function send(path: string, method: string, body?: unknown): Promise<Response> {
  const token = await tokenProvider();
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network-error', 'Server non raggiungibile: controlla la connessione');
  }
  if (response.ok) return response;
  throw await failure(response);
}

/** The error of a failed response, as the server described it. */
async function failure(response: Response): Promise<ApiError> {
  const error = (await response.json().catch(() => null)) as Partial<ApiErrorResponse> | null;
  return new ApiError(
    response.status,
    error?.code ?? 'unknown',
    error?.message ?? `Errore del server (${response.status})`,
    error?.fieldErrors,
  );
}

/**
 * Sends a file as it is (application/octet-stream), for the documents on
 * SharePoint (docs/adr/ADR-009): the API checks it and writes it.
 */
export async function apiUpload<T>(path: string, file: Blob): Promise<T> {
  const token = await tokenProvider();
  const headers: Record<string, string> = { 'content-type': 'application/octet-stream' };
  if (token) headers.authorization = `Bearer ${token}`;
  let response: Response;
  try {
    response = await fetch(`/api${path}`, { method: 'PUT', headers, body: file });
  } catch {
    throw new ApiError(0, 'network-error', 'Server non raggiungibile: controlla la connessione');
  }
  if (!response.ok) throw await failure(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export function fetchHealth(): Promise<HealthResponse> {
  return apiRequest<HealthResponse>('/health');
}

/**
 * Downloads a file the API makes (the Word documents of a quote) and hands it
 * to the browser with the name the API gives it, or `fallbackName`.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const response = await send(path, 'GET');
  const disposition = response.headers.get('content-disposition') ?? '';
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  const name = encoded ? decodeURIComponent(encoded) : fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  // Later than the click: some browsers read the URL after the event.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
